/**
 * Where a device sits relative to its inspection — shared by the device page
 * and the cards it renders.
 *
 * In-store check-ins hand the machine across the counter before the customer
 * has typed a word, so reception can inspect it straight away: the inspection
 * waits on neither the customer's details nor the diagnostic fee. Online
 * drop-offs genuinely do have to wait — nothing is on the bench yet.
 *
 * The server has never gated inspection writes on lifecycle (see
 * `update_draft_job` in sm4rt-api's `check_in_draft_job_service.py`, "Inspection
 * block — always editable"), so this only decides what the UI offers.
 */

import type { CheckInIntakeType } from '@techgitter/contracts';

const IN_STORE_INTAKE_TYPES = new Set<string>([
  'in_store',
  'in_store_return',
  'in_store_dropoff',
]);

/** True for the intake types where the device is physically present from the
 *  moment the draft exists. `express` is deliberately absent: those sessions
 *  are created straight into `inspection_in_progress`, so they never needed a
 *  way past the gate. */
export function isInStoreIntake(
  intakeType: CheckInIntakeType | string | null | undefined,
): boolean {
  return IN_STORE_INTAKE_TYPES.has(intakeType ?? '');
}

/**
 * What the session still owes when staff inspect ahead of the workflow.
 * Rendered as a note above the wizard, never as a block; null once nothing is
 * outstanding.
 *
 * Worded as "still to do", not "wait" — `awaitingMessage` tells an online
 * session to wait, which is the opposite of what a receptionist with the
 * machine in their hands should read.
 */
export function outstandingStepNote(
  lifecycle: string | null | undefined,
  paymentState: string | null | undefined,
): string | null {
  switch (lifecycle) {
    case 'draft_created':
    case 'awaiting_customer_intake':
      return 'The customer has not entered their details yet — the check-in still needs those and the terms accepted.';
    case 'intake_in_progress':
      return 'The customer is still filling in their details. The check-in needs their terms acceptance before it can be completed.';
    case 'terms_accepted':
    case 'creating_jobs':
      return 'RepairShopr records are still being created.';
    case 'job_creation_failed':
      return 'Job creation failed — tap Retry on the session before completing the check-in.';
    default:
      break;
  }
  if (paymentState === 'awaiting_payment') {
    return 'Payment has not been recorded yet — Mark Paid on the session before completing the check-in.';
  }
  if (paymentState === 'awaiting_bank_transfer') {
    return 'The bank transfer has not landed yet — record it on the session before completing the check-in.';
  }
  if (paymentState === 'failed' || lifecycle === 'payment_failed') {
    return 'Payment failed — retry it from the session page before completing the check-in.';
  }
  return null;
}
